import React from 'react';
import { X, ExternalLink } from 'lucide-react';

interface VideoItem {
  url: string;
  title: string;
  thumbnail?: string;
  author?: string;
  length?: string;
  iframe_src?: string;
  content?: string;
}

interface VideoModalProps {
  isOpen: boolean;
  video: VideoItem | null;
  onClose: () => void;
}

export function VideoModal({ isOpen, video, onClose }: VideoModalProps) {
  if (!isOpen || !video) return null;

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Determine video embed URL
  const getVideoEmbedUrl = () => {
    // YouTube
    if (video.url.includes('youtube.com/watch') || video.url.includes('youtu.be/')) {
      const videoId = video.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    // Dailymotion
    if (video.url.includes('dailymotion.com/video/')) {
      const videoId = video.url.match(/dailymotion\.com\/video\/([^/?]+)/)?.[1];
      if (videoId) {
        return `https://www.dailymotion.com/embed/video/${videoId}`;
      }
    }
    // Vimeo
    if (video.url.includes('vimeo.com/')) {
      const videoId = video.url.match(/vimeo\.com\/(\d+)/)?.[1];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
    // Use iframe_src if available
    if (video.iframe_src) {
      return video.iframe_src;
    }
    // Fallback to original URL
    return null;
  };

  const embedUrl = getVideoEmbedUrl();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {video.title}
            </h3>
            {video.author && (
              <p className="text-sm text-gray-500 mt-1">
                Tác giả: {video.author}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            <a
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Mở trong tab mới"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              aria-label="Đóng"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Video Player */}
        <div className="flex-1 overflow-auto p-4">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white p-4">
                <p className="text-lg mb-4">Không thể phát video trực tiếp</p>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Mở video trong tab mới
                </a>
              </div>
            )}
          </div>

          {/* Video Info */}
          {video.content && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{video.content}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



